import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { useSupabase } from '@/hooks/useSupabase';
import { useAuth } from '@/hooks/useAuth';
import { useUserRoles } from '@/hooks/useUserRoles';
import type { TrainingDepartment } from '@/types/training';
import { isDevCallUiEnabled } from '@/lib/devCallUi';

const STORAGE_KEY = 'reprev_active_department';

interface DepartmentContextValue {
  /** The currently active training department */
  department: TrainingDepartment;
  /** Switch active department (admin override or multi-dept user) */
  setDepartment: (dept: TrainingDepartment) => void;
  /** Departments this user is authorized to access */
  userDepartments: TrainingDepartment[];
  /** Departments the dealership has enabled */
  dealershipDepartments: TrainingDepartment[];
  /** Whether the user has access to multiple departments */
  isMultiDepartment: boolean;
  /** Whether user is admin (can switch freely regardless of assignment) */
  isAdminOverride: boolean;
  /** Loading state while fetching department assignments */
  loading: boolean;
}

const DepartmentContext = createContext<DepartmentContextValue | undefined>(undefined);

export function DepartmentProvider({ children }: { children: ReactNode }) {
  const supabase = useSupabase();
  const { userId, isSignedIn } = useAuth();
  const { isAdmin, isGlobalAdmin } = useUserRoles();
  const isDevCallUiMode = isDevCallUiEnabled();

  const [userDepartments, setUserDepartments] = useState<TrainingDepartment[]>(['sales']);
  const [dealershipDepartments, setDealershipDepartments] = useState<TrainingDepartment[]>(['sales']);
  const [loading, setLoading] = useState(false);

  // Initialize from localStorage or default to 'sales'
  const [department, setDepartmentState] = useState<TrainingDepartment>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'sales' || stored === 'service') return stored;
    return 'sales';
  });

  // Persist department choice to localStorage
  const setDepartment = useCallback((dept: TrainingDepartment) => {
    setDepartmentState(dept);
    localStorage.setItem(STORAGE_KEY, dept);
  }, []);

  // Fetch user's department assignments and dealership enabled departments
  useEffect(() => {
    if (!isSignedIn || !userId) {
      setUserDepartments(isDevCallUiMode ? ['sales', 'service'] : ['sales']);
      setDealershipDepartments(isDevCallUiMode ? ['sales', 'service'] : ['sales']);
      return;
    }

    const fetchDepartments = async () => {
      setLoading(true);
      try {
        // Fetch user's training_departments from profiles
        const { data: profile } = await supabase
          .from('profiles')
          .select('training_departments, dealership_id')
          .eq('user_id', userId)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- legacy code; typed refactor deferred
          .maybeSingle() as { data: { training_departments: string[] | null; dealership_id: string | null } | null; error: any };

        const profileDepts = (profile?.training_departments || ['sales']) as TrainingDepartment[];
        setUserDepartments(profileDepts);

        // Fetch dealership's enabled_departments
        if (profile?.dealership_id) {
          const { data: dealership } = await supabase
            .from('dealerships')
            .select('enabled_departments')
            .eq('id', profile.dealership_id)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- legacy code; typed refactor deferred
            .maybeSingle() as { data: { enabled_departments: string[] | null } | null; error: any };

          const dealerDepts = (dealership?.enabled_departments || ['sales']) as TrainingDepartment[];
          setDealershipDepartments(dealerDepts);
        }
      } catch (err) {
        console.error('[DepartmentContext] Error fetching departments:', err);
      } finally {
        setLoading(false);
      }
    };

    // Fire-and-forget: fetchDepartments handles its own errors internally.
    void fetchDepartments();
  }, [supabase, userId, isSignedIn, isDevCallUiMode]);

  // If current department is not in user's allowed list, reset to first allowed
  useEffect(() => {
    const isAdminUser = isAdmin || isGlobalAdmin || isDevCallUiMode;
    if (!isAdminUser && !userDepartments.includes(department)) {
      setDepartment(userDepartments[0] || 'sales');
    }
  }, [userDepartments, department, isAdmin, isGlobalAdmin, isDevCallUiMode, setDepartment]);

  const isAdminOverride = isAdmin || isGlobalAdmin || isDevCallUiMode;
  const isMultiDepartment = userDepartments.length > 1 || isAdminOverride;

  const value = useMemo(() => ({
    department,
    setDepartment,
    userDepartments,
    dealershipDepartments,
    isMultiDepartment,
    isAdminOverride,
    loading,
  }), [department, setDepartment, userDepartments, dealershipDepartments, isMultiDepartment, isAdminOverride, loading]);

  return (
    <DepartmentContext.Provider value={value}>
      {children}
    </DepartmentContext.Provider>
  );
}

export function useDepartment() {
  const context = useContext(DepartmentContext);
  if (!context) {
    throw new Error('useDepartment must be used within a DepartmentProvider');
  }
  return context;
}

export function useDepartmentSafe() {
  return useContext(DepartmentContext);
}
